import CustomButton from "@components/buttons/CustomButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { invoiceServices } from "@services/apis/Invoice";
import { AddOfframpBankRequest } from "@services/models/InvoiceApprovals/AddOfframpBankModel";
import { useEffect, useState } from "react";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

interface BankManagementSliderProps {
  isOpen: boolean;
  onClose: () => void;
  onBankAdded?: () => void;
}

const BankManagementSlider: React.FC<BankManagementSliderProps> = ({
  isOpen,
  onClose,
  onBankAdded,
}) => {
  const [bankName, setBankName] = useState<string>("");
  const [accountNo, setAccountNo] = useState<string>("");
  const [accountName, setAccountName] = useState<string>("");
  const [accountType, setAccountType] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { handleError, handleSuccess } = useSnackbarMessage();

  const handleSubmit = async () => {
    if (!bankName.trim()) {
      handleError({ message: "Please enter bank name" });
      return;
    }
    if (!accountNo.trim()) {
      handleError({ message: "Please enter account number" });
      return;
    }
    if (!accountName.trim()) {
      handleError({ message: "Please enter account name" });
      return;
    }
    if (!accountType.trim()) {
      handleError({ message: "Please enter account type" });
      return;
    }

    try {
      setIsLoading(true);
      const request: AddOfframpBankRequest = {
        bankName: bankName.trim(),
        accountNo: accountNo.trim(),
        accountName: accountName.trim(),
        accountType: accountType.trim(),
        isActive: true,
      };

      await invoiceServices.addOfframpBank(request);

      handleSuccess({ message: "Bank added successfully" });

      setBankName("");
      setAccountNo("");
      setAccountName("");
      setAccountType("");

      if (onBankAdded) {
        onBankAdded();
      }

      onClose();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.msg || "Failed to add bank";
      handleError({ message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setBankName("");
    setAccountNo("");
    setAccountName("");
    setAccountType("");
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const isFormValid = Boolean(
    bankName.trim() &&
      accountNo.trim() &&
      accountName.trim() &&
      accountType.trim()
  );

  return (
    <SlidingPanel
      isOpen={isOpen}
      heading={"Add Bank"}
      onClose={() => {
        resetForm();
        onClose();
      }}
      onClickCross={() => {
        resetForm();
        onClose();
      }}
      showConfirm={false}
      isSlider={true}
      widthDesktop={"35%"}
    >
      <div className="flex flex-col px-[16px] py-[16px] mt-[60px]">
        <div className="flex flex-col gap-4 bg-bg-base rounded-lg p-4 border border-border-subtle">
          <GlobalTextInputField
            placeholder="Enter bank name"
            value={bankName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setBankName(e.target.value)
            }
            title="Bank Name"
          />

          <GlobalTextInputField
            placeholder="Enter account number"
            value={accountNo}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setAccountNo(e.target.value)
            }
            title="Account Number"
          />

          <GlobalTextInputField
            placeholder="Enter account holder name"
            value={accountName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setAccountName(e.target.value)
            }
            title="Account Name"
          />

          <GlobalTextInputField
            placeholder="Enter account type (e.g., Business Checking)"
            value={accountType}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setAccountType(e.target.value)
            }
            title="Account Type"
          />

          <CustomButton
            text="ADD BANK"
            onClick={handleSubmit}
            isActive={isFormValid && !isLoading}
            isButtonLoading={isLoading}
            className="flex-1 w-full"
          />
        </div>
      </div>
    </SlidingPanel>
  );
};

export default BankManagementSlider;
